import type { SpacedRepetitionData } from '../types/diagnostic';
import type { AgeGroup } from '../types/child';
import type { Course } from '../data/courses';

/**
 * SM-2 inspired spaced repetition for skill review scheduling.
 *
 * After each review:
 * - Correct: increase interval and ease factor
 * - Incorrect: reset to short interval and decrease ease factor
 */

const MIN_EASE_FACTOR = 1.3;
const DEFAULT_EASE_FACTOR = 2.5;
const MIN_INTERVAL = 1; // days

export function createInitialRepetition(skillId: string): SpacedRepetitionData {
  return {
    skillId,
    interval: 1,
    easeFactor: DEFAULT_EASE_FACTOR,
    nextReviewDate: new Date().toISOString().split('T')[0],
    repetitions: 0,
    lastReviewDate: new Date().toISOString().split('T')[0],
  };
}

/**
 * Update spaced repetition data after a review.
 * Quality: 0-5 scale (0=total fail, 5=perfect).
 * For diagnostic quizzes: correct=4, incorrect=1
 */
export function updateRepetition(
  data: SpacedRepetitionData,
  quality: number,
): SpacedRepetitionData {
  const today = new Date().toISOString().split('T')[0];
  const q = Math.max(0, Math.min(5, quality));

  let { interval, easeFactor, repetitions } = data;

  if (q >= 3) {
    // Correct response
    repetitions += 1;
    if (repetitions === 1) {
      interval = 1;
    } else if (repetitions === 2) {
      interval = 3;
    } else {
      interval = Math.round(interval * easeFactor);
    }

    // Adjust ease factor
    easeFactor = easeFactor + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02));
    easeFactor = Math.max(MIN_EASE_FACTOR, easeFactor);
  } else {
    // Incorrect response — reset
    repetitions = 0;
    interval = MIN_INTERVAL;
    easeFactor = Math.max(MIN_EASE_FACTOR, easeFactor - 0.2);
  }

  // Cap interval at 90 days for kids (they forget faster than adults)
  interval = Math.min(interval, 90);

  const nextDate = new Date();
  nextDate.setDate(nextDate.getDate() + interval);

  return {
    ...data,
    interval,
    easeFactor,
    repetitions,
    nextReviewDate: nextDate.toISOString().split('T')[0],
    lastReviewDate: today,
  };
}

/**
 * Check if a skill is due for review today or overdue.
 */
export function isDueForReview(data: SpacedRepetitionData): boolean {
  const today = new Date().toISOString().split('T')[0];
  return data.nextReviewDate <= today;
}

/**
 * Get all skills due for review, sorted by most overdue first.
 */
export function getSkillsDueForReview(
  repetitions: Record<string, SpacedRepetitionData>,
): SpacedRepetitionData[] {
  const today = new Date().toISOString().split('T')[0];
  return Object.values(repetitions)
    .filter(r => r.nextReviewDate <= today)
    .sort((a, b) => a.nextReviewDate.localeCompare(b.nextReviewDate));
}

/**
 * Convert diagnostic result correctness to SM-2 quality score.
 * correct + fast = 5, correct + slow = 4, correct = 3
 * incorrect = 1, incorrect + very slow = 0
 */
export function diagnosticResultToQuality(correct: boolean, responseTimeMs: number): number {
  if (correct) {
    if (responseTimeMs < 5000) return 5;  // Quick and correct
    if (responseTimeMs < 15000) return 4; // Correct with some thought
    return 3;                              // Correct but slow
  } else {
    if (responseTimeMs < 3000) return 1;  // Quick wrong answer (guessing)
    return 0;                              // Slow and wrong
  }
}

/**
 * Calculate a review urgency score for activity-selector integration.
 * Higher = more urgent. Returns 0 if not due.
 */
export function getReviewUrgencyScore(data: SpacedRepetitionData): number {
  const today = new Date();
  const reviewDate = new Date(data.nextReviewDate + 'T00:00:00');
  const daysPast = Math.floor((today.getTime() - reviewDate.getTime()) / (1000 * 60 * 60 * 24));

  if (daysPast < 0) return 0; // Not due yet
  if (daysPast === 0) return 10;
  if (daysPast <= 3) return 20;
  if (daysPast <= 7) return 35;
  return 50; // Very overdue
}

// ═══════════════════════════════════════════════════════════════════════════
// FLASHCARD LAYER — Leitner 5-box scheduling for kid-friendly review sessions
//
// This layer sits above the skill-level SM-2 system above. Flashcards are
// derived automatically from course fact cards, lab fun-facts and diagnostic
// questions; see docs/improvements/content/03-spaced-repetition.md.
//
// All functions below are pure (no Date.now / no I/O); callers pass `today`
// explicitly so the engine is trivially unit-testable.
// ═══════════════════════════════════════════════════════════════════════════

/** Where a flashcard came from — preserved so UI can deep-link back to lesson. */
export type FlashcardSource =
  | { kind: 'course-fact'; courseId: string; lessonId: string; factIndex: number }
  | { kind: 'activity-exit-ticket'; activityId: string; tier: AgeGroup; questionIndex: number; tiedObjective: string }
  | { kind: 'lab-fact'; labId: string; itemId: string; factIndex: number }
  | { kind: 'diagnostic'; questionId: string; skillId: string }
  | { kind: 'quiz-miss'; questionId: string; subject: string; category?: string };

/** A single reviewable flashcard. Pure data — no review state attached. */
export interface Flashcard {
  id: string;
  prompt: string;
  /** Full answer text shown on card flip. */
  answer: string;
  /** Optional multiple-choice options (used for diagnostic-derived cards). */
  options?: string[];
  /** Lowest age group this card is appropriate for. */
  ageGroup: AgeGroup;
  /** Free-form tags for filtering (subject, skill, category). */
  tags: string[];
  source: FlashcardSource;
  /** Optional emoji/image hint for visual Pre-K cards. */
  emoji?: string;
}

/** Leitner box number. 1 = most fragile, 5 = nearly mastered. */
export type LeitnerBox = 1 | 2 | 3 | 4 | 5;

/** Per-card review state. Keyed by flashcard id in the caller's store. */
export interface ReviewState {
  cardId: string;
  box: LeitnerBox;
  /** ISO date (YYYY-MM-DD) when this card is next due. */
  nextReviewDate: string;
  /** ISO date of last review, or empty string if never reviewed. */
  lastReviewDate: string;
  /** Total reviews for this card (for analytics). */
  reviewCount: number;
  /** Graduated cards (promoted past Box 5) drop out of daily rotation
   *  but may re-surface via the SM-2 maintenance layer. */
  graduated: boolean;
}

/** Result of a single review attempt. */
export interface ReviewResult {
  cardId: string;
  /** True = moves up a box; False = reset to Box 1. */
  correct: boolean;
  /** ISO date of the review (caller-supplied for testability). */
  reviewedOn: string;
}

/** Fixed Leitner interval schedule (days). Matches the design doc. */
export const LEITNER_INTERVALS: Record<LeitnerBox, number> = {
  1: 1,
  2: 3,
  3: 7,
  4: 14,
  5: 30,
};

/** Age-group -> (softCap, hardCap) for daily reviews. */
export const REVIEW_CAPS: Record<AgeGroup, { soft: number; hard: number }> = {
  'pre-k': { soft: 5, hard: 8 },
  'kinder': { soft: 8, hard: 12 },
  'early-elementary': { soft: 12, hard: 18 },
  'upper-elementary': { soft: 15, hard: 20 },
};

function addDays(isoDate: string, days: number): string {
  const d = new Date(isoDate + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().split('T')[0];
}

/** Create a fresh review state for a flashcard that has never been seen. */
export function createInitialReviewState(cardId: string, today: string): ReviewState {
  return {
    cardId,
    box: 1,
    nextReviewDate: today, // Due immediately
    lastReviewDate: '',
    reviewCount: 0,
    graduated: false,
  };
}

/**
 * Core scheduling function. Pure — no Date.now, no I/O.
 *
 * Correct: advance one box, schedule `LEITNER_INTERVALS[newBox]` days out.
 *          Cards correct at Box 5 become `graduated`.
 * Wrong:   reset to Box 1, schedule 1 day out.
 */
export function scheduleNextReview(state: ReviewState, result: ReviewResult): ReviewState {
  if (state.cardId !== result.cardId) {
    throw new Error(`scheduleNextReview: cardId mismatch (${state.cardId} vs ${result.cardId})`);
  }

  const reviewCount = state.reviewCount + 1;

  if (result.correct) {
    if (state.box === 5) {
      // Already at top box — graduate
      return {
        ...state,
        box: 5,
        graduated: true,
        lastReviewDate: result.reviewedOn,
        nextReviewDate: addDays(result.reviewedOn, 90), // maintenance interval
        reviewCount,
      };
    }
    const newBox = (state.box + 1) as LeitnerBox;
    return {
      ...state,
      box: newBox,
      graduated: false,
      lastReviewDate: result.reviewedOn,
      nextReviewDate: addDays(result.reviewedOn, LEITNER_INTERVALS[newBox]),
      reviewCount,
    };
  }

  // Incorrect — reset.
  return {
    ...state,
    box: 1,
    graduated: false,
    lastReviewDate: result.reviewedOn,
    nextReviewDate: addDays(result.reviewedOn, LEITNER_INTERVALS[1]),
    reviewCount,
  };
}

/**
 * Choose which cards to review today.
 *
 * Priority:
 *   1. Overdue cards, most-overdue first (then lowest box first).
 *   2. Due-today cards, lowest box first (fragile cards win).
 *   3. New cards (no state), capped at 30% of the session.
 *   4. Deterministic tie-break by card id.
 *
 * Graduated cards are excluded unless their 90-day maintenance date has hit.
 */
export function selectDueCards(
  allCards: Flashcard[],
  reviewStates: Record<string, ReviewState>,
  maxCards: number,
  today: string = new Date().toISOString().split('T')[0],
): Flashcard[] {
  if (maxCards <= 0) return [];

  const overdue: Array<{ card: Flashcard; state: ReviewState }> = [];
  const dueToday: Array<{ card: Flashcard; state: ReviewState }> = [];
  const fresh: Flashcard[] = [];

  for (const card of allCards) {
    const state = reviewStates[card.id];
    if (!state) {
      fresh.push(card);
      continue;
    }
    if (state.graduated && state.nextReviewDate > today) continue;
    if (state.nextReviewDate < today) {
      overdue.push({ card, state });
    } else if (state.nextReviewDate === today) {
      dueToday.push({ card, state });
    }
  }

  overdue.sort((a, b) => {
    if (a.state.nextReviewDate !== b.state.nextReviewDate) {
      return a.state.nextReviewDate.localeCompare(b.state.nextReviewDate);
    }
    if (a.state.box !== b.state.box) return a.state.box - b.state.box;
    return a.card.id.localeCompare(b.card.id);
  });

  dueToday.sort((a, b) => {
    if (a.state.box !== b.state.box) return a.state.box - b.state.box;
    return a.card.id.localeCompare(b.card.id);
  });

  fresh.sort((a, b) => a.id.localeCompare(b.id));

  const freshQuota = Math.max(1, Math.floor(maxCards * 0.3));
  const freshTaken = fresh.slice(0, freshQuota);

  const reviewSlots = maxCards - freshTaken.length;
  const reviewPool = [...overdue, ...dueToday].slice(0, reviewSlots).map(x => x.card);

  // If there weren't enough review cards, backfill with more fresh cards.
  const result = [...reviewPool, ...freshTaken];
  if (result.length < maxCards) {
    const remaining = fresh.slice(freshTaken.length, freshTaken.length + (maxCards - result.length));
    result.push(...remaining);
  }
  return result.slice(0, maxCards);
}

// ─── Derivation helpers ────────────────────────────────────────────────────

/**
 * Attempt a simple cloze-deletion: blank out the first numeric run or the
 * first capitalized multi-word phrase in `fact`. Returns null if no clozable
 * token is found. Pure.
 */
export function tryClozeFact(fact: string): { prompt: string; answer: string } | null {
  // Prefer numbers (with commas/decimals) — highest-signal factoids for kids.
  const numMatch = fact.match(/\b\d[\d,]*(?:\.\d+)?\b/);
  if (numMatch && numMatch.index !== undefined) {
    const prompt =
      fact.slice(0, numMatch.index) + '____' + fact.slice(numMatch.index + numMatch[0].length);
    return { prompt, answer: numMatch[0] };
  }
  return null;
}

/**
 * Extract flashcards from a single Course. Walks every lesson and every
 * age-group fact list. For upper-elementary we attempt cloze deletion so the
 * prompt is a fill-in-the-blank rather than bare recall.
 */
export function deriveFlashcardsFromCourse(course: Course): Flashcard[] {
  const out: Flashcard[] = [];
  const ageGroups: AgeGroup[] = ['pre-k', 'kinder', 'early-elementary', 'upper-elementary'];

  for (const lesson of course.lessons) {
    for (const ageGroup of ageGroups) {
      const facts = lesson.facts[ageGroup] || [];
      facts.forEach((fact, factIndex) => {
        const useCloze = ageGroup === 'upper-elementary';
        const cloze = useCloze ? tryClozeFact(fact) : null;

        const prompt =
          cloze?.prompt ??
          (ageGroup === 'pre-k' || ageGroup === 'kinder'
            ? `${lesson.emoji} ${lesson.title} — what do you remember?`
            : `Fact about ${lesson.title}:`);
        const answer = cloze?.answer ?? fact;

        out.push({
          id: `course:${course.id}:${lesson.id}:${ageGroup}:${factIndex}`,
          prompt,
          answer,
          ageGroup,
          tags: [course.subject, course.category, ...(course.futureSkills ?? [])],
          source: {
            kind: 'course-fact',
            courseId: course.id,
            lessonId: lesson.id,
            factIndex,
          },
          emoji: lesson.emoji,
        });
      });
    }
  }

  return out;
}

/** Convert a ReviewResult into the diagnostic-layer SM-2 quality score so
 *  the existing `updateRepetition` can keep the skill-level ease factor in
 *  sync for diagnostic-derived flashcards. */
export function reviewResultToQuality(result: ReviewResult): number {
  return result.correct ? 4 : 1;
}

// ═══════════════════════════════════════════════════════════════════════════
// EXAMPLE SEED — 20 flashcards derived from real courses.ts content.
//
// This is a static hand-picked sample chosen to exercise every prompt style
// (cloze, visual pre-k, kinder recall, upper-elem recall) across a few
// lessons. Used by the dev sandbox and as a fixture for tests; NOT the
// production deck (the real deck is built at runtime by calling
// `deriveFlashcardsFromCourse` over the full course list).
// ═══════════════════════════════════════════════════════════════════════════

export const EXAMPLE_FLASHCARDS: Flashcard[] = [
  // ── Dinosaur Discovery → T-Rex ──
  {
    id: 'example:dino-trex:pre-k:0',
    prompt: '🦖 T-Rex: King of the Dinosaurs — what do you remember?',
    answer: 'T-Rex was one of the biggest meat-eating dinosaurs ever!',
    ageGroup: 'pre-k',
    tags: ['science'],
    source: { kind: 'course-fact', courseId: 'dinosaur-discovery', lessonId: 'dino-trex', factIndex: 0 },
    emoji: '🦖',
  },
  {
    id: 'example:dino-trex:pre-k:2',
    prompt: '🦖 T-Rex: King of the Dinosaurs — what do you remember?',
    answer: 'T-Rex was as tall as a giraffe!',
    ageGroup: 'pre-k',
    tags: ['science'],
    source: { kind: 'course-fact', courseId: 'dinosaur-discovery', lessonId: 'dino-trex', factIndex: 2 },
    emoji: '🦖',
  },
  {
    id: 'example:dino-trex:kinder:0',
    prompt: '🦖 T-Rex: King of the Dinosaurs — what do you remember?',
    answer: 'T-Rex was as tall as a giraffe — about as tall as the tallest animal you can see at the zoo!',
    ageGroup: 'kinder',
    tags: ['science'],
    source: { kind: 'course-fact', courseId: 'dinosaur-discovery', lessonId: 'dino-trex', factIndex: 0 },
    emoji: '🦖',
  },
  {
    id: 'example:dino-trex:kinder:1',
    prompt: '🦖 T-Rex: King of the Dinosaurs — what do you remember?',
    answer: 'T-Rex had tiny arms that were only about 3 feet long.',
    ageGroup: 'kinder',
    tags: ['science'],
    source: { kind: 'course-fact', courseId: 'dinosaur-discovery', lessonId: 'dino-trex', factIndex: 1 },
    emoji: '🦖',
  },
  {
    id: 'example:dino-trex:kinder:3',
    prompt: '🦖 T-Rex: King of the Dinosaurs — what do you remember?',
    answer: 'T-Rex lived about 68 million years ago.',
    ageGroup: 'kinder',
    tags: ['science'],
    source: { kind: 'course-fact', courseId: 'dinosaur-discovery', lessonId: 'dino-trex', factIndex: 3 },
    emoji: '🦖',
  },
  {
    id: 'example:dino-trex:early:1',
    prompt: 'Fact about T-Rex: King of the Dinosaurs:',
    answer: 'T-Rex could run at about 15-25 miles per hour despite its huge size.',
    ageGroup: 'early-elementary',
    tags: ['science'],
    source: { kind: 'course-fact', courseId: 'dinosaur-discovery', lessonId: 'dino-trex', factIndex: 1 },
    emoji: '🦖',
  },
  {
    id: 'example:dino-trex:early:2',
    prompt: 'Fact about T-Rex: King of the Dinosaurs:',
    answer: 'T-Rex teeth were up to 12 inches long, including the root.',
    ageGroup: 'early-elementary',
    tags: ['science'],
    source: { kind: 'course-fact', courseId: 'dinosaur-discovery', lessonId: 'dino-trex', factIndex: 2 },
    emoji: '🦖',
  },
  {
    id: 'example:dino-trex:upper:0',
    // Cloze-deleted: the number "40" is blanked.
    prompt: 'Tyrannosaurus Rex was approximately ____ feet long and weighed up to 9 tons (18,000 pounds).',
    answer: '40',
    ageGroup: 'upper-elementary',
    tags: ['science'],
    source: { kind: 'course-fact', courseId: 'dinosaur-discovery', lessonId: 'dino-trex', factIndex: 0 },
    emoji: '🦖',
  },
  {
    id: 'example:dino-trex:upper:1',
    prompt: 'T-Rex had a bite force of about ____ pounds, strong enough to crush bone.',
    answer: '12,800',
    ageGroup: 'upper-elementary',
    tags: ['science'],
    source: { kind: 'course-fact', courseId: 'dinosaur-discovery', lessonId: 'dino-trex', factIndex: 1 },
    emoji: '🦖',
  },
  {
    id: 'example:dino-trex:upper:2',
    prompt: 'The first T-Rex fossil was discovered in ____ by Barnum Brown in Montana.',
    answer: '1902',
    ageGroup: 'upper-elementary',
    tags: ['science'],
    source: { kind: 'course-fact', courseId: 'dinosaur-discovery', lessonId: 'dino-trex', factIndex: 2 },
    emoji: '🦖',
  },
  {
    id: 'example:dino-trex:upper:3',
    prompt: 'Fact about T-Rex: King of the Dinosaurs:',
    answer: 'T-Rex had binocular vision, meaning it could see in 3D like humans, which helped it hunt.',
    ageGroup: 'upper-elementary',
    tags: ['science'],
    source: { kind: 'course-fact', courseId: 'dinosaur-discovery', lessonId: 'dino-trex', factIndex: 3 },
    emoji: '🦖',
  },
  // ── Diagnostic-derived sample cards ──
  {
    id: 'example:diag:trex-food',
    prompt: 'What did T-Rex eat?',
    answer: 'Meat',
    options: ['Meat', 'Vegetables', 'Cookies'],
    ageGroup: 'pre-k',
    tags: ['science', 'dinosaurs'],
    source: { kind: 'diagnostic', questionId: 'trex-food', skillId: 'science.dinosaurs.basic' },
    emoji: '🦖',
  },
  {
    id: 'example:diag:trex-size',
    prompt: 'How big was T-Rex?',
    answer: 'As tall as a giraffe',
    options: ['As big as a cat', 'As tall as a giraffe', 'As big as an ant'],
    ageGroup: 'pre-k',
    tags: ['science', 'dinosaurs'],
    source: { kind: 'diagnostic', questionId: 'trex-size', skillId: 'science.dinosaurs.basic' },
    emoji: '🦒',
  },
  {
    id: 'example:diag:trex-arms',
    prompt: 'What was special about T-Rex arms?',
    answer: 'They were very tiny',
    options: ['They were very long', 'They were very tiny', 'They could fly'],
    ageGroup: 'kinder',
    tags: ['science', 'dinosaurs'],
    source: { kind: 'diagnostic', questionId: 'trex-arms', skillId: 'science.dinosaurs.basic' },
    emoji: '🦖',
  },
  {
    id: 'example:diag:trex-meaning',
    prompt: 'What does "Tyrannosaurus Rex" mean?',
    answer: 'Tyrant lizard king',
    options: ['Big lizard', 'Tyrant lizard king', 'Fast runner', 'Tiny arms'],
    ageGroup: 'early-elementary',
    tags: ['science', 'language', 'dinosaurs'],
    source: { kind: 'diagnostic', questionId: 'trex-meaning', skillId: 'language.etymology' },
    emoji: '🦖',
  },
  {
    id: 'example:diag:trex-period',
    prompt: 'During which period did T-Rex live?',
    answer: 'Cretaceous',
    options: ['Triassic', 'Jurassic', 'Cretaceous', 'Ice Age'],
    ageGroup: 'early-elementary',
    tags: ['science', 'dinosaurs', 'geology'],
    source: { kind: 'diagnostic', questionId: 'trex-period', skillId: 'science.geology.eras' },
    emoji: '🦖',
  },
  {
    id: 'example:diag:trex-teeth-length',
    prompt: 'How long were T-Rex teeth?',
    answer: 'Up to 12 inches',
    options: ['1 inch', '6 inches', 'Up to 12 inches', '3 feet'],
    ageGroup: 'early-elementary',
    tags: ['science', 'dinosaurs'],
    source: { kind: 'diagnostic', questionId: 'trex-teeth-length', skillId: 'science.dinosaurs.anatomy' },
    emoji: '🦷',
  },
  {
    id: 'example:diag:trex-speed',
    prompt: 'How fast could T-Rex run?',
    answer: '15-25 mph',
    options: ['5 mph', '15-25 mph', '100 mph', 'It could not run'],
    ageGroup: 'early-elementary',
    tags: ['science', 'dinosaurs'],
    source: { kind: 'diagnostic', questionId: 'trex-speed', skillId: 'science.dinosaurs.anatomy' },
    emoji: '💨',
  },
  // ── A couple more Pre-K recall cards rounding out the 20 ──
  {
    id: 'example:dino-trex:pre-k:1',
    prompt: '🦖 T-Rex: King of the Dinosaurs — what do you remember?',
    answer: 'T-Rex had tiny little arms but a HUGE mouth full of teeth!',
    ageGroup: 'pre-k',
    tags: ['science'],
    source: { kind: 'course-fact', courseId: 'dinosaur-discovery', lessonId: 'dino-trex', factIndex: 1 },
    emoji: '🦖',
  },
  {
    id: 'example:dino-trex:kinder:2',
    prompt: '🦖 T-Rex: King of the Dinosaurs — what do you remember?',
    answer: 'T-Rex had the strongest bite of any land animal ever!',
    ageGroup: 'kinder',
    tags: ['science'],
    source: { kind: 'course-fact', courseId: 'dinosaur-discovery', lessonId: 'dino-trex', factIndex: 2 },
    emoji: '🦖',
  },
];
